import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, canSee } from "@/context/AuthContext";
import { ChangePasswordButton } from "@/components/ChangePasswordButton";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, ShoppingBag, Package, TrendingUp, Factory,
  Sun, Moon, LogOut, Undo2, Users, Menu, ShieldCheck, History, Layers, BellRing, Instagram, PackageCheck, Ruler,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Panoramica", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/ordini", label: "Ordini", icon: ShoppingBag, id: "ordini" },
  { to: "/clienti", label: "Clienti", icon: Users, id: "clienti" },
  { to: "/magazzino", label: "Prodotti", icon: Package, id: "magazzino" },
  { to: "/vendite", label: "Vendite", icon: TrendingUp, id: "vendite" },
  { to: "/resi", label: "Resi", icon: Undo2, id: "resi" },
  { to: "/produzione", label: "Produzione", icon: Factory, id: "produzione" },
  { to: "/materie-prime", label: "Materie prime", icon: Layers, id: "materie_prime" },
  { to: "/modellistica", label: "Modellistica", icon: Ruler, id: "modellistica" },
  { to: "/richieste", label: "Richieste clienti", icon: BellRing, id: "richieste" },
  { to: "/social", label: "Social", icon: Instagram, id: "social" },
  { to: "/logistica", label: "Logistica", icon: PackageCheck, id: "logistica" },
  { to: "/attivita", label: "Registro attività", icon: History, id: "attivita" },
  { to: "/utenti", label: "Utenti", icon: ShieldCheck, id: "utenti" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      {menuOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-overlay" />}
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border/60 bg-card transition-transform lg:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex flex-col items-center border-b border-border/60 px-6 py-5">
          <img src="/logo.png" alt="La Mora Glamour" className="h-auto w-36 dark:invert" data-testid="sidebar-logo" />
          <p className="mt-2 pl-[0.3em] text-center font-mono-erp text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Gestionale</p>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.filter((item) => canSee(user, item.id)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.id}`}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border/60 p-3">
          <div className="flex items-center gap-3 rounded-md px-3 py-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-semibold uppercase">
              {(user?.name || "A")[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{user?.name || "Utente"}</p>
              <p className="truncate text-[11px] text-muted-foreground">{user?.email}</p>
            </div>
            <ChangePasswordButton />
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="min-w-0 flex-1 lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-2 border-b border-border/60 bg-background/80 px-4 py-3.5 backdrop-blur-md lg:px-8">
          <div className="flex min-w-0 items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
            <p className="truncate font-mono-erp text-xs uppercase tracking-widest text-muted-foreground">
              Gestionale Abbigliamento · Produzione & Vendita
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle">
              {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={doLogout} data-testid="logout-btn">
              <LogOut className="h-[18px] w-[18px]" />
            </Button>
          </div>
        </header>
        <main className="px-4 py-6 lg:px-8 lg:py-8">{children}</main>
      </div>
    </div>
  );
}
